import {
  DEFAULT_APP_NAME,
  DEFAULT_RELEASE,
  SinkName,
  type MonitoringConfig,
  type MonitoringEnv,
} from './types';

/** Returns a trimmed env value, or `undefined` when missing/blank. */
const readEnv = (value: string | undefined): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
};

const KNOWN_SINKS = new Set<string>(Object.values(SinkName));

/**
 * Parses `VITE_MONITORING_SINKS` — a comma-separated list of sink names.
 *
 * Unknown names are dropped with a warning rather than throwing: a typo in an
 * env var must not take the app down, and an unrecognised sink is most likely a
 * value from a branch where that sink exists.
 *
 * `none` anywhere in the list wins, so a single value can switch monitoring off
 * without editing the rest.
 */
export const parseSinks = (value: string | undefined): SinkName[] => {
  const raw = readEnv(value);
  if (!raw) {
    return [];
  }

  const requested = raw
    .split(',')
    .map((name) => name.trim().toLowerCase())
    .filter((name) => name !== '');

  const unknown = requested.filter((name) => !KNOWN_SINKS.has(name));
  if (unknown.length > 0) {
    console.warn(`[monitoring] ignoring unknown sink(s): ${unknown.join(', ')}`);
  }

  const known = requested.filter((name): name is SinkName => KNOWN_SINKS.has(name));
  if (known.includes(SinkName.None)) {
    return [];
  }

  // De-duplicated, order preserved.
  return [...new Set(known)];
};

/**
 * Builds vendor-neutral configuration from `VITE_*` variables.
 *
 * Returns `null` when no sink is requested, which disables monitoring: no sink
 * is constructed and nothing touches the network. That is the committed default
 * everywhere, and it is what keeps unit tests and Playwright offline.
 *
 * Deliberately not routed through `notNull()` (`libs/hooks/src/config.ts`),
 * which throws at import and blanks the page.
 *
 * Pure, so it is testable without stubbing `import.meta.env`.
 */
export const buildConfig = (env: MonitoringEnv): MonitoringConfig | null => {
  const sinks = parseSinks(env.VITE_MONITORING_SINKS);

  if (sinks.length === 0) {
    return null;
  }

  return {
    sinks,
    appName: readEnv(env.VITE_MONITORING_APP_NAME) ?? DEFAULT_APP_NAME,
    release: readEnv(env.VITE_RELEASE) ?? DEFAULT_RELEASE,
  };
};
